/* eslint-disable @typescript-eslint/no-floating-promises, parog-web/no-glint-expect-error-directive */
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import dayjs from 'dayjs';
import {
  validateDate,
  validateLength,
  validatePresence
} from 'ember-changeset-validations/validators';
import type { BufferedChangeset } from 'ember-changeset/types';
import type IntlService from 'ember-intl/services/intl';
import saveSendMessage from 'parog-web/components/chat/utils/save-send-message';
import Content from 'parog-web/components/document/edition-form/content';
import FileUploadedEditionFormContentComponent from 'parog-web/components/file-uploaded/edition-form/content';
import type { ForcBaseArgs } from 'parog-web/components/forc/abstract/base-component';
import type {
  Payload as ForcPayload,
  ToastMessage
} from 'parog-web/components/forc/abstract/journal-event-based-component';
import JournalEventBasedComponent from 'parog-web/components/forc/abstract/journal-event-based-component';
import Provider from 'parog-web/components/forc/provider';
import BioExamPrescribedJournalEvent from 'parog-web/models/bio-exam-prescribed-journal-event';
import BioExamResultsReceivedJournalEvent from 'parog-web/models/bio-exam-results-received-journal-event';
import ConsultationReportWrittenJournalEvent from 'parog-web/models/consultation-report-written-journal-event';
import type DpSessionDeclaredJournalEvent from 'parog-web/models/dp-session-declared-journal-event';
import FileUploadedJournalEvent from 'parog-web/models/file-uploaded-journal-event';
import type MedicalEventJournalEvent from 'parog-web/models/medical-event-journal-event';
import MedicalImagingPrescribedJournalEvent from 'parog-web/models/medical-imaging-prescribed-journal-event';
import MedicationPrescribedJournalEvent from 'parog-web/models/medication-prescribed-journal-event';
import ParamedicalCarePrescribedJournalEvent from 'parog-web/models/paramedical-care-prescribed-journal-event';
import type Patient from 'parog-web/models/patient';
import type AnalyticsService from 'parog-web/services/analytics';
import type FetcherService from 'parog-web/services/fetcher';
import type SemeiaSessionService from 'parog-web/services/session';
import validateSometimes from 'parog-web/validators/sometimes';
import emberSwal from 'ui-kit/utils/ember-swal';

type JournalEventModelNameEnum =
  | 'consultation-report-written-journal-event'
  | 'bio-exam-results-received-journal-event'
  | 'file-uploaded-journal-event'
  | 'bio-exam-prescribed-journal-event'
  | 'medication-prescribed-journal-event'
  | 'medical-imaging-prescribed-journal-event'
  | 'paramedical-care-prescribed-journal-event';

interface DocumentEditionFormComponentArgs extends ForcBaseArgs {
  patient: Patient;
}

export interface Period {
  duration: number;
  unit: 'day' | 'days' | 'week' | 'weeks' | 'month' | 'months';
}

interface Model extends ForcPayload {
  willSendMessage: boolean;
  chatMessage: string;
}

/* eslint-disable parog-web/migrate-non-patch-forms */
export default class DocumentEditionFormComponent extends JournalEventBasedComponent<DocumentEditionFormComponentArgs> {
  static override forcDisplayName;
  static override fullComponentId = 'document/edition-form';

  @service declare analytics: AnalyticsService;
  @service declare fetcher: FetcherService;
  @service declare intl: IntlService;
  @service override requestManager;
  @service declare router: RouterService;
  @service declare session: SemeiaSessionService;

  override journalEventProperties = [
    'occurredAt',
    'comments',
    'fileAttachmentsAttributes'
  ];
  validityOptionsMedication: Period[] = [
    { duration: 1, unit: 'day' },
    { duration: 2, unit: 'days' },
    { duration: 3, unit: 'days' },
    { duration: 4, unit: 'days' },
    { duration: 5, unit: 'days' },
    { duration: 6, unit: 'days' },
    { duration: 7, unit: 'days' },
    { duration: 8, unit: 'days' },
    { duration: 9, unit: 'days' },
    { duration: 10, unit: 'days' },
    { duration: 11, unit: 'days' },
    { duration: 12, unit: 'days' },
    { duration: 13, unit: 'days' },
    { duration: 1, unit: 'week' },
    { duration: 2, unit: 'weeks' },
    { duration: 3, unit: 'weeks' },
    { duration: 1, unit: 'month' },
    { duration: 2, unit: 'months' },
    { duration: 3, unit: 'months' },
    { duration: 4, unit: 'months' },
    { duration: 5, unit: 'months' },
    { duration: 6, unit: 'months' }
  ];

  /**
   * The date label must change when bio exam
   * is selected.
   */
  get dateLabel(): string {
    return this.documentType === 'bio'
      ? "Date de l'examen"
      : 'Date du document';
  }

  /**
   * The document type from the changeset.
   */
  get documentType(): string {
    return this.changeset.get('documentType');
  }

  override get forcDisplayName(): string {
    return this.session.medic
      ? 'Envoyer un document au patient'
      : 'Envoyer un document à votre équipe de soin';
  }

  /**
   * This getter gives the mapping between
   * selected document type and related JE.
   */
  get items(): {
    [key: string]: {
      text: string;
      journalEventModelName: string | Array<string>;
    };
  } {
    return {
      bio: {
        text: 'Résultats de biologie',
        journalEventModelName: 'bio-exam-results-received-journal-event'
      },
      cr: {
        text: 'Compte-rendu',
        journalEventModelName: 'consultation-report-written-journal-event'
      },
      prescr: {
        text: 'Ordonnances',
        journalEventModelName: [
          'bio-exam-prescribed-journal-event',
          'medication-prescribed-journal-event',
          'medical-imaging-prescribed-journal-event',
          'paramedical-care-prescribed-journal-event'
        ]
      },
      other: {
        text: 'Autre',
        journalEventModelName: 'file-uploaded-journal-event'
      }
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  override get journalEventClassType(): any {
    return this.documentType === 'prescr'
      ? (this.changeset.get('fileSubType') as JournalEventModelNameEnum)
      : (this.items[this.changeset.get('documentType')]
          .journalEventModelName as JournalEventModelNameEnum);
  }

  override get toastMessage(): ToastMessage | null {
    return this.session.patient
      ? {
          message: 'Votre document a bien été envoyé.',
          title: 'Document'
        }
      : super.toastMessage;
  }

  override get validations(): Record<string, unknown> {
    return {
      documentType: validatePresence({
        presence: true,
        description: 'Le type de document'
      }),
      occurredAt: [
        validatePresence({
          presence: true,
          description: 'La date'
        }),
        validateDate({
          after: new Date('1900-01-01'),
          message: 'La date ne peut pas être avant le 01/01/1900.'
        }),
        validateDate({
          onOrBefore: dayjs().endOf('day').toDate(),
          message: 'La date ne peut pas être dans le futur.'
        })
      ],
      fileAttachmentsAttributes: validatePresence({
        presence: true,
        description: 'Le document'
      }),
      title: validateSometimes(
        [
          validatePresence({
            presence: true,
            description: 'Le titre'
          }),
          validateLength({
            allowBlank: false,
            max: FileUploadedEditionFormContentComponent.titleMaximumChars,
            description: 'Le titre'
          })
        ],
        function (this: BufferedChangeset) {
          /* eslint-disable-next-line */
          return this.get('documentType') === 'other';
        }
      ),
      fileSubType: validateSometimes(
        [
          validatePresence({
            presence: true,
            description: 'Le type de fichier'
          })
        ],
        function (this: BufferedChangeset) {
          return (
            /* eslint-disable-next-line */
            this.get('documentType') === 'other' ||
            /* eslint-disable-next-line */
            this.get('documentType') === 'prescr'
          );
        }
      ),
      consultationType: validateSometimes(
        [
          validatePresence({
            presence: true,
            description: 'Le type de consultation'
          })
        ],
        function (this: BufferedChangeset) {
          /* eslint-disable-next-line */
          return this.get('documentType') === 'cr';
        }
      )
    };
  }

  get validityOptionsBioExam() {
    return [
      'Un mois',
      'Deux mois',
      'Trois mois',
      'Quatre mois',
      'Cinq mois',
      'Six mois'
    ];
  }

  override afterCompleted = (): void => {
    if (!this.session.medic) this.router.transitionTo('/patient-web/chat');
    else {
      const tab = this.changeset.get('willSendMessage')
        ? 'consult-chat'
        : 'consult-timeline';
      this.router.transitionTo({
        queryParams: { tab }
      });
    }
  };

  override computePayload(): ForcPayload {
    const payload = super.computePayload();

    if (this.documentType === 'other') {
      payload.title = this.changeset.get('title');
      payload.fileSubType = this.changeset.get('fileSubType');
      payload.body = this.changeset.get('body') || null;
    }

    if (this.documentType === 'cr') {
      payload.consultationType = this.changeset.get('consultationType');
      payload.questions = this.changeset.get('questions') || null;
    }

    if (this.documentType === 'bio') {
      payload.normalized = true;
    }

    if (
      this.documentType === 'prescr' &&
      [
        'bio-exam-prescribed-journal-event',
        'medication-prescribed-journal-event'
      ].includes(this.changeset.get('fileSubType'))
    ) {
      payload.validityPeriod =
        this.changeset.get('fileSubType') ===
        'bio-exam-prescribed-journal-event'
          ? parseInt(this.changeset.get('validityPeriod'))
          : this.changeset.get('validityPeriod');
    }

    return payload;
  }

  onDocumentTypeUpdate = (documentType: string): void => {
    const fileSubType = this.changeset.get('fileSubType');
    if (documentType == 'other') {
      if (!fileSubType) {
        // Initialize fileSubType if "other" is selected
        this.changeset.set('fileSubType', 'other');
      }
    }

    if (documentType != 'other') {
      // Reinitialize fileSubType if "other" is not selected
      if (!fileSubType) return;

      this.changeset.set('fileSubType', undefined);
    }
  };

  override async save(): Promise<
    | BioExamResultsReceivedJournalEvent
    | ConsultationReportWrittenJournalEvent
    | FileUploadedJournalEvent
    | BioExamPrescribedJournalEvent
    | MedicationPrescribedJournalEvent
    | MedicalImagingPrescribedJournalEvent
    | ParamedicalCarePrescribedJournalEvent
    | MedicalEventJournalEvent
    | DpSessionDeclaredJournalEvent
    | undefined
  > {
    try {
      const journalEvent = await super.save();

      if (journalEvent) {
        await this.store.findRecord(
          this.journalEventClassType,
          journalEvent.id,
          {
            include: 'file-attachments'
          }
        );

        if (!this.changeset.get('willSendMessage') && !this.session.patient)
          return journalEvent;

        let chatMessage;
        switch (true) {
          case journalEvent instanceof ConsultationReportWrittenJournalEvent:
            chatMessage = 'Compte-rendu envoyé';
            break;
          case journalEvent instanceof BioExamResultsReceivedJournalEvent:
            chatMessage = "Résultats d'examen envoyés";
            break;
          case journalEvent instanceof FileUploadedJournalEvent:
            chatMessage = 'Fichier envoyé';
            break;
          case journalEvent instanceof BioExamPrescribedJournalEvent ||
            journalEvent instanceof MedicationPrescribedJournalEvent ||
            journalEvent instanceof MedicalImagingPrescribedJournalEvent ||
            journalEvent instanceof ParamedicalCarePrescribedJournalEvent:
            chatMessage = 'Ordonnance envoyée';
            break;
          default:
            break;
        }

        await this.store
          .createRecord('patient-chat-message-sent-journal-event', {
            occurredAt: new Date(),
            triggeringJournalEvent: journalEvent,
            patient: journalEvent.patient,
            creator: journalEvent.creator,
            body: chatMessage
          })
          .save();

        await saveSendMessage(this.model as Model, {
          store: this.store,
          patient: this.patient,
          analytics: this.analytics
        });
      }

      await this.fetcher.query('patient-chat-message-sent-journal-event', {
        filter: {
          patient_id: this.args.patient.id
        },
        include: 'creator'
      });

      return journalEvent;
    } catch (error) {
      this.throwUploadErrorMessage(error);
      throw error;
    }
  }

  throwUploadErrorMessage(error) {
    if (this.documentType === 'bio' && error.errors[0].status == 409) {
      emberSwal.fire({
        title: 'Erreur',
        html: `Des résultats d'analyse biologiques identiques existent déjà à cette date.<br/><br>
        Vous les retrouverez dans <strong>l'historique des bilans de biologie</strong>.`,
        icon: 'error'
      });
    } else {
      emberSwal.fire({
        title: "Oups, échec de l'importation de votre document.",
        text: 'Veuillez réessayer ou contacter le support Sêmeia si le problème persiste.',
        icon: 'error'
      });
    }
  }

  <template>
    {{! @glint-expect-error }}
    <Provider @isHeaderHidden={{@displayedFromPanel}} @forcComponent={{this}}>
      <:content as |Form|>
        <Content
          @forcComponent={{this}}
          @form={{Form}}
          {{! @glint-expect-error }}
          @displayedFromPanel={{@displayedFromPanel}}
          @items={{this.items}}
          @patient={{@patient}}
          @onDocumentTypeUpdate={{this.onDocumentTypeUpdate}}
          @validityOptionsMedication={{this.validityOptionsMedication}}
          @validityOptionsBioExam={{this.validityOptionsBioExam}}
        />
      </:content>
      <:footer as |Footer|>
        {{! @glint-expect-error }}
        {{#unless @displayedFromPanel}}
          <Footer.CancelButton />
        {{/unless}}
        <Footer.SubmitButton />
      </:footer>
    </Provider>
  </template>
}
